import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../../utils/api';

const JOIN_TYPES = [
  { key: 'left', label: 'LEFT JOIN', desc: 'Keep all your existing records and attach matching data from the new table. Records without a match will show blank values for the new columns.' },
  { key: 'inner', label: 'INNER JOIN', desc: 'Only include records that have a match in both tables. Records without a match on either side are excluded from results.' },
  { key: 'full', label: 'FULL OUTER JOIN', desc: 'Include everything from both tables, whether or not there is a match. Useful when you want to see gaps on either side.' },
];

const CONDITION_OPERATORS = [
  { key: 'eq', label: '=' },
  { key: 'neq', label: '!=' },
  { key: 'gt', label: '>' },
  { key: 'gte', label: '>=' },
  { key: 'lt', label: '<' },
  { key: 'lte', label: '<=' },
];

export default function CustomJoinPanel({
  reportType,
  customJoins,
  onChangeCustomJoins,
  schema,
}) {
  const [joinableData, setJoinableData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchJoinable = useCallback(async () => {
    if (!reportType) return;
    setLoading(true);
    try {
      const res = await reportsApi.joinableTables(reportType);
      setJoinableData(res.data);
    } catch (e) {
      console.error('Failed to load joinable tables:', e);
    } finally {
      setLoading(false);
    }
  }, [reportType]);

  useEffect(() => {
    fetchJoinable();
  }, [fetchJoinable]);

  if (!reportType || !joinableData) {
    return loading ? (
      <div className="text-xs text-gray-600 py-2">Loading joinable tables...</div>
    ) : null;
  }

  const { joinableTables, fkEdges, existingTables, allTables } = joinableData;

  // Compute which tables are currently in play (existing + custom joined)
  const currentTables = new Set(existingTables);
  for (const cj of customJoins) {
    if (cj.table) currentTables.add(cj.table);
  }

  // Get available tables for a new join (connected to current tables, not already joined)
  const getAvailableTables = (currentJoinIndex) => {
    const inPlay = new Set(existingTables);
    for (let i = 0; i < customJoins.length; i++) {
      if (i !== currentJoinIndex && customJoins[i].table) {
        inPlay.add(customJoins[i].table);
      }
    }

    // Find tables connected to any in-play table via FK edges
    const available = [];
    for (const t of joinableTables) {
      if (inPlay.has(t.key)) continue;
      const hasEdge = fkEdges.some(e =>
        (e.tableA === t.key && inPlay.has(e.tableB)) ||
        (e.tableB === t.key && inPlay.has(e.tableA))
      );
      if (hasEdge) available.push(t);
    }

    // Also check tables not in joinableTables but connected to custom-joined tables
    if (allTables) {
      for (const [key, t] of Object.entries(allTables)) {
        if (inPlay.has(key)) continue;
        if (available.find(a => a.key === key)) continue;
        const hasEdge = fkEdges.some(e =>
          (e.tableA === key && inPlay.has(e.tableB)) ||
          (e.tableB === key && inPlay.has(e.tableA))
        );
        if (hasEdge) available.push(t);
      }
    }

    return available;
  };

  // Get FK-suggested conditions for a join
  const getSuggestedConditions = (tableKey) => {
    const suggestions = [];
    for (const edge of fkEdges) {
      if (edge.tableA === tableKey && currentTables.has(edge.tableB)) {
        suggestions.push({
          leftColumn: edge.columnA,
          operator: 'eq',
          rightTable: edge.tableB,
          rightColumn: edge.columnB,
        });
      } else if (edge.tableB === tableKey && currentTables.has(edge.tableA)) {
        suggestions.push({
          leftColumn: edge.columnB,
          operator: 'eq',
          rightTable: edge.tableA,
          rightColumn: edge.columnA,
        });
      }
    }
    return suggestions;
  };

  // Get columns for a table (from allTables or joinableTables)
  const getTableColumns = (tableKey) => {
    const t = allTables?.[tableKey] || joinableTables.find(jt => jt.key === tableKey);
    return t?.columns || [];
  };

  // Get columns from existing/joined tables for the right side of conditions
  const getRightSideColumns = (excludeTable) => {
    const cols = [];

    // Base report type columns
    if (schema?.columns) {
      for (const c of schema.columns) {
        cols.push({ table: '__base__', tableLabel: schema.label, key: c.key, label: c.label, type: c.type });
      }
    }

    return cols;
  };

  const addJoin = () => {
    if (customJoins.length >= 3) return;
    onChangeCustomJoins([...customJoins, { table: '', joinType: 'left', conditions: [] }]);
  };

  const updateJoin = (idx, patch) => {
    const updated = [...customJoins];
    updated[idx] = { ...updated[idx], ...patch };
    onChangeCustomJoins(updated);
  };

  const removeJoin = (idx) => {
    onChangeCustomJoins(customJoins.filter((_, i) => i !== idx));
  };

  const selectTable = (idx, tableKey) => {
    const suggestions = getSuggestedConditions(tableKey);
    updateJoin(idx, {
      table: tableKey,
      conditions: suggestions.length > 0 ? suggestions : [{ leftColumn: '', operator: 'eq', rightTable: '__base__', rightColumn: '' }],
    });
  };

  const updateCondition = (joinIdx, condIdx, patch) => {
    const updated = [...customJoins];
    const conditions = [...updated[joinIdx].conditions];
    conditions[condIdx] = { ...conditions[condIdx], ...patch };
    updated[joinIdx] = { ...updated[joinIdx], conditions };
    onChangeCustomJoins(updated);
  };

  const addCondition = (joinIdx) => {
    const updated = [...customJoins];
    updated[joinIdx] = {
      ...updated[joinIdx],
      conditions: [...updated[joinIdx].conditions, { leftColumn: '', operator: 'eq', rightTable: '__base__', rightColumn: '' }],
    };
    onChangeCustomJoins(updated);
  };

  const removeCondition = (joinIdx, condIdx) => {
    const updated = [...customJoins];
    updated[joinIdx] = {
      ...updated[joinIdx],
      conditions: updated[joinIdx].conditions.filter((_, i) => i !== condIdx),
    };
    onChangeCustomJoins(updated);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Custom Joins
          <span className="ml-1 text-gray-600 normal-case font-normal">up to 3 tables</span>
        </h3>
        <button
          onClick={addJoin}
          disabled={customJoins.length >= 3}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add Join
        </button>
      </div>

      {customJoins.length === 0 && (
        <p className="text-xs text-gray-600 italic">No custom joins configured. Add a join to combine additional tables.</p>
      )}

      <div className="space-y-3">
        {customJoins.map((cj, idx) => {
          const availableTables = getAvailableTables(idx);
          const leftColumns = cj.table ? getTableColumns(cj.table) : [];
          const rightColumns = getRightSideColumns(cj.table);
          const tableDef = cj.table ? (allTables?.[cj.table] || joinableTables.find(t => t.key === cj.table)) : null;

          return (
            <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">Join {idx + 1}</span>
                <button
                  onClick={() => removeJoin(idx)}
                  className="text-gray-600 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              </div>

              {/* Join type selector */}
              <div className="flex items-center gap-2 mb-2">
                {JOIN_TYPES.map(jt => (
                  <button
                    key={jt.key}
                    onClick={() => updateJoin(idx, { joinType: jt.key })}
                    title={jt.desc}
                    className={`px-2 py-1 rounded text-[10px] font-mono font-medium ${
                      cj.joinType === jt.key
                        ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {jt.label}
                  </button>
                ))}
              </div>

              {/* Table selector */}
              <div className="mb-2">
                <select
                  value={cj.table}
                  onChange={e => selectTable(idx, e.target.value)}
                  className="w-full border border-gray-300 rounded text-xs px-2 py-1.5 bg-white"
                >
                  <option value="">Select table to join...</option>
                  {availableTables.map(t => (
                    <option key={t.key} value={t.key}>
                      {t.label} ({t.category})
                    </option>
                  ))}
                </select>
              </div>

              {/* Join conditions */}
              {cj.table && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-medium text-gray-600 uppercase">ON conditions</span>
                    <button
                      onClick={() => addCondition(idx)}
                      className="text-[10px] text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + AND
                    </button>
                  </div>

                  {cj.conditions.map((cond, condIdx) => (
                    <div key={condIdx} className="flex items-center gap-1 mb-1">
                      {condIdx > 0 && <span className="text-[10px] text-gray-600 w-6">AND</span>}

                      {/* Left side (custom join table column) */}
                      <select
                        value={cond.leftColumn}
                        onChange={e => updateCondition(idx, condIdx, { leftColumn: e.target.value })}
                        className="border border-gray-300 rounded text-[11px] px-1 py-1 flex-1 min-w-0 bg-white"
                      >
                        <option value="">{tableDef?.label || 'Table'}...</option>
                        {leftColumns.map(c => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>

                      {/* Operator */}
                      <select
                        value={cond.operator}
                        onChange={e => updateCondition(idx, condIdx, { operator: e.target.value })}
                        className="border border-gray-300 rounded text-[11px] px-1 py-1 w-12 bg-white"
                      >
                        {CONDITION_OPERATORS.map(op => (
                          <option key={op.key} value={op.key}>{op.label}</option>
                        ))}
                      </select>

                      {/* Right side (existing table column) */}
                      <select
                        value={cond.rightColumn ? `${cond.rightTable}::${cond.rightColumn}` : ''}
                        onChange={e => {
                          const [rt, rc] = e.target.value.split('::');
                          updateCondition(idx, condIdx, { rightTable: rt, rightColumn: rc });
                        }}
                        className="border border-gray-300 rounded text-[11px] px-1 py-1 flex-1 min-w-0 bg-white"
                      >
                        <option value="">Base table...</option>
                        {rightColumns.map(c => (
                          <option key={`${c.table}::${c.key}`} value={`${c.table}::${c.key}`}>
                            {c.label}
                          </option>
                        ))}
                      </select>

                      {cj.conditions.length > 1 && (
                        <button
                          onClick={() => removeCondition(idx, condIdx)}
                          className="text-gray-300 hover:text-red-500 p-0.5"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}

                  {/* Preview joined columns */}
                  {leftColumns.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <span className="text-[10px] text-gray-600">
                        Adds {leftColumns.length} columns from {tableDef?.label}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
